import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState, useEffect } from "react";
import { apiClient } from "@/services/api";
import { toast } from "sonner";
import { UserProfile } from "@/hooks/useUsers";

interface ClientProject {
  id: string;
  name: string;
  status?: string;
}

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  user: UserProfile | null;
}

export function EditUserModal({ isOpen, onClose, onSuccess, user }: EditUserModalProps) {
  const [fullName, setFullName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [role, setRole] = useState<'admin' | 'developer' | 'viewer'>('viewer');
  const [clientProjectId, setClientProjectId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [clientProjects, setClientProjects] = useState<ClientProject[]>([]);

  // Initialize form with user data
  useEffect(() => {
    if (user && isOpen) {
      setFullName(user.fullName || "");
      setAvatarUrl(user.avatarUrl || "");
      setRole(user.role);
      setClientProjectId(user.clientProjectId || "");
    }
  }, [user, isOpen]);

  // Fetch client projects from API
  useEffect(() => {
    const fetchClientProjects = async () => {
      try {
        const response = await apiClient.get('/clients?status=active');
        if (response.data?.success && response.data?.data?.clients) {
          setClientProjects(response.data.data.clients);
        }
      } catch (error) {
        console.error('Error fetching client projects:', error);
        toast.error('Error al cargar proyectos');
      }
    };

    if (isOpen) {
      fetchClientProjects();
    }
  }, [isOpen]);

  const handleUpdate = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError("");

    // Validation
    if (role !== "admin" && !clientProjectId) {
      setError("Debe seleccionar un proyecto cliente para usuarios no administradores.");
      setIsLoading(false);
      return;
    }

    try {
      const updates: any = {
        fullName: fullName || undefined,
        avatarUrl: avatarUrl || undefined,
        role,
      };

      // Only include clientProjectId if role is not admin
      if (role === 'admin') {
        updates.clientProjectId = null;
      } else {
        updates.clientProjectId = clientProjectId;
      }

      const response = await apiClient.put(`/users/${user.id}`, updates);
      
      if (response.data?.success) {
        toast.success('Usuario actualizado exitosamente');
        onSuccess?.();
        onClose();
      } else {
        throw new Error(response.data?.error?.message || 'Error al actualizar usuario');
      }
    } catch (error: any) {
      console.error("Error updating user:", error);
      const errorMessage = error.response?.data?.error?.message || error.message || "Error al actualizar usuario";
      
      // Check for specific permission errors
      if (errorMessage.includes('Only admins can change user roles')) {
        setError('Solo los administradores pueden cambiar roles de usuario');
      } else if (errorMessage.includes('Only admins can change client project')) {
        setError('Solo los administradores pueden cambiar asignaciones de proyecto');
      } else {
        setError(errorMessage);
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setError("");
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Editar Usuario</DialogTitle>
          <DialogDescription>
            Actualiza la información del usuario. Los cambios se aplicarán inmediatamente.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label className="text-right">Email</Label>
            <Input 
              value={user.email} 
              disabled 
              className="col-span-3 bg-muted" 
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="fullName" className="text-right">
              Nombre Completo
            </Label>
            <Input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="col-span-3"
              placeholder="Nombre completo"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="avatarUrl" className="text-right">
              Foto de Perfil
            </Label>
            <Input
              id="avatarUrl"
              type="url"
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              className="col-span-3"
              placeholder="URL de la imagen (opcional)"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="role" className="text-right">
              Rol
            </Label>
            <Select onValueChange={(value) => setRole(value as any)} value={role}>
              <SelectTrigger className="col-span-3">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Administrador</SelectItem>
                <SelectItem value="developer">Desarrollador</SelectItem>
                <SelectItem value="viewer">Visualizador</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {role !== "admin" && (
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="client-project" className="text-right">
                Proyecto Cliente
              </Label>
              <Select onValueChange={setClientProjectId} value={clientProjectId}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Selecciona un proyecto" />
                </SelectTrigger>
                <SelectContent>
                  {clientProjects.length === 0 ? (
                    <SelectItem value="loading" disabled>Cargando proyectos...</SelectItem>
                  ) : (
                    clientProjects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}
          {error && <p className="text-red-500 text-sm col-span-4 text-center">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleUpdate} 
            disabled={isLoading || (role !== 'admin' && !clientProjectId)}
          >
            {isLoading ? "Actualizando..." : "Actualizar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}